import type { OpenPanel } from '@openpanel/sdk';
import { config } from '../config';

let clientPromise: Promise<OpenPanel> | undefined;

export function initOpenPanel(): void {
  if (clientPromise || !config.OPENPANEL_CLIENT_ID || !config.OPENPANEL_API_URL) {
    return;
  }

  // Load analytics outside the boot-critical bundle. Calls made while it loads
  // are replayed in call order by callbacks on this shared promise.
  clientPromise = import('@openpanel/sdk').then(({ OpenPanel }) => {
    const client = new OpenPanel({
      clientId: config.OPENPANEL_CLIENT_ID,
      apiUrl: config.OPENPANEL_API_URL,
      sdk: 'web',
    });
    return client;
  });
}

function withClient(action: (client: OpenPanel) => void | Promise<unknown>): void {
  initOpenPanel();
  if (!clientPromise) {
    return;
  }
  void clientPromise.then(action).catch(() => {
    // Analytics must never affect clinician workflows.
  });
}

export function trackOpenPanelEvent(
  name: string,
  properties: Record<string, string | number | boolean>,
): void {
  withClient(client => client.track(name, {
    ...properties,
    clinician_portal_version: 'v1',
  }));
}

export function trackOpenPanelView(path: string): void {
  // Supply both fields directly so patient IDs and document titles cannot leak.
  withClient(client => client.track('screen_view', {
    __path: path,
    __title: path,
    clinician_portal_version: 'v1',
  }));
}

export function identifyOpenPanelClinician(
  id: string,
  properties: Record<string, string>,
): void {
  withClient(client => client.identify({ profileId: id, properties }));
}

export function clearOpenPanelClinician(): void {
  withClient(client => client.clear());
}
